import React, { useState } from 'react';
import { toast, Toaster } from 'react-hot-toast';
import styles from './AdminAddRecipeBook.module.css';
import IconPlaceholder from '@/ui/assets/icons/ImageCreateReceta.svg';
import { Categories } from "@/data/categories.js";
import { saveRecipe } from '@services/adminRecipeBookLocalService';
import CategoryButton from '@/ui/components/client/CategoryButton/CategoryButton';

const AddRecipeBookPage = () => {

    const initialState = {
        name: '',
        ingredients: [],
        steps: [],
        cover: null,
        coverFile: null,
        gallery: [],
        galleryFiles: []
    };

    const [recipe, setRecipe] = useState(initialState);

    const [newIng, setNewIng] = useState({
        amount: '',
        item: ''
    });

    const [newStep, setNewStep] = useState('');

    /* CATEGORY STATE */
    const [activeCategory, setActiveCategory] = useState('popular');

    /**
     * Media Management Handlers
     */
    const handleCoverChange = (e) => {
        const file = e.target.files[0];

        if (!file) return;

        setRecipe(prev => ({
            ...prev,
            cover: URL.createObjectURL(file),
            coverFile: file
        }));

        e.target.value = '';
    };

    const handleGalleryChange = (e) => {
        const files = Array.from(e.target.files);

        if (files.length === 0) return;

        const previewUrls = files.map(file =>
            URL.createObjectURL(file)
        );

        setRecipe(prev => ({
            ...prev,
            gallery: [
                ...(prev.gallery || []),
                ...previewUrls
            ],
            galleryFiles: [
                ...(prev.galleryFiles || []),
                ...files
            ]
        }));

        e.target.value = '';
    };

    /**
     * INGREDIENTS
     */
    const handleAddIng = () => {
        if (newIng.item) {

            setRecipe({
                ...recipe,
                ingredients: [
                    ...recipe.ingredients,
                    {
                        ...newIng,
                        checked: false
                    }
                ]
            });

            setNewIng({
                amount: '',
                item: ''
            });

        } else {
            toast.error("Please enter an ingredient name");
        }
    };

    /**
     * STEPS
     */
    const handleAddStep = () => {

        if (newStep.trim()) {

            setRecipe({
                ...recipe,
                steps: [
                    ...recipe.steps,
                    {
                        step: recipe.steps.length + 1,
                        description: newStep
                    }
                ]
            });

            setNewStep('');

        } else {
            toast.error("Step description cannot be empty");
        }
    };

    const handleRemoveStep = (index) => {

        const newList = recipe.steps
            .filter((_, i) => i !== index)
            .map((s, i) => ({
                ...s,
                step: i + 1
            }));

        setRecipe({
            ...recipe,
            steps: newList
        });
    };

    /**
     * REMOVE GALLERY
     */
    const handleRemoveGallery = (index) => {

        setRecipe({
            ...recipe,
            gallery: recipe.gallery.filter((_, i) => i !== index),
            galleryFiles: recipe.galleryFiles.filter((_, i) => i !== index)
        });
    };

    /**
     * REMOVE INGREDIENT
     */
    const handleRemoveIng = (index) => {

        setRecipe({
            ...recipe,
            ingredients: recipe.ingredients.filter((_, i) => i !== index)
        });
    };

    /**
     * SAVE
     */
    const handleSave = async () => {

        if (!recipe.name) {
            return toast.error(
                "The name is a required field."
            );
        }

        const loadingToast = toast.loading(
            "Saving recipe..."
        );

        try {

            const result = await saveRecipe(recipe);

            if (result.success) {

                toast.success(
                    "Recipe saved successfully!",
                    {
                        id: loadingToast
                    }
                );

                setRecipe(initialState);

            } else {

                toast.error(
                    "Failed to save recipe",
                    {
                        id: loadingToast
                    }
                );
            }

        } catch (error) {

            toast.error(
                "An unexpected error occurred",
                {
                    id: loadingToast
                }
            );

            console.error(
                "Save Operation Error:",
                error
            );
        }
    };

    return (
        <main className={styles.addRecipeUi}>

            <Toaster position="top-center" />

            <div className={styles.addRecipeCard}>

                <div className={styles.addRecipeContent}>

                    <h2 className={styles.addRecipeTitle}>
                        Recetas del local
                    </h2>

                    <input
                        type="file"
                        id="cover-upload"
                        style={{ display: 'none' }}
                        onChange={handleCoverChange}
                        accept="image/*"
                    />

                    {/* TOP */}
                    <div className={styles.addRecipeTop}>

                        <div
                            className={styles.addRecipeImage}
                            onClick={() =>
                                document
                                    .getElementById('cover-upload')
                                    .click()
                            }
                            style={{ cursor: 'pointer' }}
                        >

                            {recipe.cover ? (

                                <img
                                    src={recipe.cover}
                                    alt="preview"
                                />

                            ) : (

                                <img
                                    src={IconPlaceholder}
                                    alt="placeholder"
                                    className={styles.iconPlaceholder}
                                />
                            )}

                        </div>

                        {/* INFO */}
                        <div className={styles.addRecipeInfo}>

                            <label className={styles.addRecipeLabel}>
                                Nombre del producto
                            </label>

                            <input
                                className={styles.addRecipeInput}
                                placeholder="Milkshake de Chocolate"
                                value={recipe.name}
                                onChange={(e) =>
                                    setRecipe({
                                        ...recipe,
                                        name: e.target.value
                                    })
                                }
                            />

                            {/* CATEGORIES */}
                            <div className={styles.categories}>

                                {Categories.map((category) => (

                                    <CategoryButton
                                        key={category.id}
                                        category={category}
                                        isActive={
                                            activeCategory === category.id
                                        }
                                        onClick={() =>
                                            setActiveCategory(category.id)
                                        }
                                    />

                                ))}

                            </div>

                        </div>

                    </div>

                    {/* INGREDIENTES */}
                    <h3>Ingredientes</h3>

                    <div className={styles.addRecipeList}>

                        {recipe.ingredients.map((ing, i) => (

                            <div
                                key={i}
                                className={styles.addRecipeItem}
                            >

                                <input
                                    type="checkbox"
                                    className={styles.addRecipeCheckbox}
                                />

                                <span className={styles.ingredientText}>
                                    {ing.amount} {ing.item}
                                </span>

                                <button
                                    type="button"
                                    className={styles.addRecipeDelete}
                                    onClick={() => handleRemoveIng(i)}
                                >
                                    ✕
                                </button>

                            </div>

                        ))}

                    </div>

                    <div className={styles.addRecipeRow}>

                        <input
                            placeholder="200 ml"
                            value={newIng.amount}
                            onChange={(e) =>
                                setNewIng({
                                    ...newIng,
                                    amount: e.target.value
                                })
                            }
                        />

                        <input
                            placeholder="leche"
                            value={newIng.item}
                            onChange={(e) =>
                                setNewIng({
                                    ...newIng,
                                    item: e.target.value
                                })
                            }
                        />

                        <button
                            type="button"
                            onClick={handleAddIng}
                        >
                            Añadir
                        </button>

                    </div>

                    {/* PREPARATION */}
                    <h3>Preparación</h3>

                    <div className={styles.stepsList}>

                        {recipe.steps.map((s, i) => (

                            <div
                                key={i}
                                className={styles.stepItem}
                            >

                                <span className={styles.addRecipeStep}>
                                    {s.step}.
                                </span>

                                <span className={styles.stepText}>
                                    {s.description}
                                </span>

                                <button
                                    type="button"
                                    className={styles.addRecipeDelete}
                                    onClick={() => handleRemoveStep(i)}
                                >
                                    ✕
                                </button>

                            </div>

                        ))}

                    </div>

                    <div className={`${styles.addRecipeRow} ${styles.single}`}>

                        <input
                            placeholder="Descripción del paso"
                            value={newStep}
                            onChange={(e) =>
                                setNewStep(e.target.value)
                            }
                        />

                        <button
                            type="button"
                            onClick={handleAddStep}
                        >
                            Añadir
                        </button>

                    </div>

                    {/* GALERÍA */}
                    <h3>
                        Galería <span>(opcional)</span>
                    </h3>

                    <input
                        type="file"
                        id="gallery-upload"
                        style={{ display: 'none' }}
                        multiple
                        accept="image/*"
                        onChange={handleGalleryChange}
                    />

                    <button
                        type="button"
                        className={styles.addRecipeGalleryBtn}
                        onClick={() =>
                            document
                                .getElementById('gallery-upload')
                                .click()
                        }
                    >
                        Añadir a la galería
                    </button>

                    <div className={styles.addRecipeGallery}>

                        {recipe.gallery.map((img, i) => (

                            <div
                                key={i}
                                className={styles.addRecipeBox}
                            >

                                <img
                                    src={img}
                                    alt={`img-${i}`}
                                />

                                <button
                                    type="button"
                                    className={styles.addRecipeBoxDelete}
                                    onClick={() =>
                                        handleRemoveGallery(i)
                                    }
                                >
                                    ✕
                                </button>

                            </div>

                        ))}

                    </div>

                </div>

                {/* BOTÓN */}
                <div className={styles.addRecipeActions}>

                    <button
                        type="button"
                        className={styles.addRecipeSave}
                        onClick={handleSave}
                    >
                        Guardar receta
                    </button>

                </div>

            </div>

        </main>
    );
};

export default AddRecipeBookPage;
